import { useState, useEffect } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useAuth } from "@project/hooks/useAuth";
import { PageShell } from "@project/user/components/page-shell";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Phone,
  ShieldCheck,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@project/components/ui/input-otp";

export function SignupPage() {
  const { requestSignupOtp, verifySignupOtp } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirectTo?: string };
  const redirectTo = search.redirectTo ?? "/";

  const [step, setStep] = useState<1 | 2>(1);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [codeReference, setCodeReference] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    dob: "",
    gender: "Male",
    username: "",
  });

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (step === 2 && timer > 0) {
      interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  const buildSignupPayload = () => ({
    first_name: formData.firstName.trim(),
    last_name: formData.lastName.trim(),
    email: formData.email.trim(),
    date_of_birth: formData.dob,
    gender: formData.gender,
    phone_number: phoneNumber.replace(/\D/g, ""),
    username: formData.username.trim(),
  });

  const validateAccountForm = () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      toast.error("First Name and Last Name are required");
      return false;
    }
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (!formData.dob) {
      toast.error("Date of Birth is required");
      return false;
    }
    if (!formData.username.trim()) {
      toast.error("Username is required");
      return false;
    }
    const cleanPhone = phoneNumber.replace(/\D/g, "");
    if (cleanPhone.length !== 10) {
      toast.error("Please enter a valid 10-digit phone number");
      return false;
    }
    return true;
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateAccountForm()) {
      return;
    }

    setLoading(true);
    try {
      const codeRef = await requestSignupOtp(buildSignupPayload());
      setCodeReference(codeRef);
      setStep(2);
      setTimer(30);
      setCanResend(false);
      toast.success("OTP sent to your mobile number");
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) {
      return;
    }
    setLoading(true);
    try {
      const codeRef = await requestSignupOtp(buildSignupPayload());
      setCodeReference(codeRef);
      setTimer(30);
      setCanResend(false);
      toast.success("OTP resent successfully");
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error("Please enter the 6-digit OTP");
      return;
    }

    setLoading(true);
    try {
      await verifySignupOtp(otp, codeReference, buildSignupPayload());
      if (redirectTo.includes("?")) {
        const [path, searchStr] = redirectTo.split("?");
        const searchParams = new URLSearchParams(searchStr);
        const searchObj: Record<string, string> = {};
        searchParams.forEach((val, key) => {
          searchObj[key] = val;
        });
        navigate({ to: path as any, search: searchObj as any });
      } else {
        navigate({ to: redirectTo as any });
      }
    } catch (err: any) {
      toast.error(
        err?.message ?? "Failed to complete signup. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const fieldInputClass =
    "h-11 rounded-xl border-input/80 bg-primary/[0.04] shadow-sm";

  return (
    <PageShell>
      <div className="flex min-h-[calc(100vh-12rem)] items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg">
          <div
            className="border-border/80 anim-zoom-in relative overflow-hidden rounded-3xl border bg-card p-8 shadow-xl transition-all"
            style={{ animationDelay: "120ms" }}
          >
            <div className="bg-tricolor absolute left-0 right-0 top-0 h-1.5" />

            <div className="mb-6 text-center">
              <div className="bg-primary/10 mx-auto mb-3 grid h-14 w-14 place-items-center rounded-2xl text-primary">
                {step === 1 && <Phone className="h-7 w-7" />}
                {step === 2 && <ShieldCheck className="h-7 w-7" />}
              </div>
              <h1 className="font-serif text-3xl font-semibold tracking-tight text-primary">
                {step === 1 && "Create Your Account"}
                {step === 2 && "Verify Phone Number"}
              </h1>
              <p className="text-primary/70 mt-1.5 text-sm">
                {step === 1 &&
                  "Fill in your details to create your Jivandhara account"}
                {step === 2 &&
                  `Enter the 6-digit OTP sent to +91 ${phoneNumber}`}
              </p>
            </div>

            <div className="mb-8 flex items-center justify-center gap-2">
              <div
                className={`h-2.5 rounded-full transition-all ${
                  step === 1 ? "w-8 bg-primary" : "bg-primary/30 w-2.5"
                }`}
              />
              <div
                className={`h-2.5 rounded-full transition-all ${
                  step === 2 ? "w-8 bg-primary" : "bg-primary/30 w-2.5"
                }`}
              />
            </div>

            {step === 1 && (
              <form onSubmit={handleSendOtp} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      placeholder="Harshal"
                      value={formData.firstName}
                      onChange={e =>
                        setFormData({ ...formData, firstName: e.target.value })
                      }
                      className={fieldInputClass}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      placeholder="Thakar"
                      value={formData.lastName}
                      onChange={e =>
                        setFormData({ ...formData, lastName: e.target.value })
                      }
                      className={fieldInputClass}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="harshal@example.com"
                    value={formData.email}
                    onChange={e =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className={fieldInputClass}
                    required
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="dob">Date of Birth</Label>
                    <Input
                      id="dob"
                      type="date"
                      value={formData.dob}
                      onChange={e =>
                        setFormData({ ...formData, dob: e.target.value })
                      }
                      className={fieldInputClass}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="gender">Gender</Label>
                    <Select
                      value={formData.gender}
                      onValueChange={val =>
                        setFormData({ ...formData, gender: val })
                      }
                    >
                      <SelectTrigger id="gender" className={fieldInputClass}>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    placeholder="Harshalt"
                    value={formData.username}
                    onChange={e =>
                      setFormData({ ...formData, username: e.target.value })
                    }
                    className={fieldInputClass}
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="phone">Phone Number</Label>
                  <div className="focus-within:ring-primary/30 mt-0.5 flex rounded-xl border border-input bg-background shadow-sm focus-within:ring-2">
                    <span className="flex items-center rounded-l-xl border-r border-input bg-muted px-3.5 text-sm font-medium text-muted-foreground">
                      +91
                    </span>
                    <Input
                      id="phone"
                      type="tel"
                      maxLength={10}
                      placeholder="7698343695"
                      value={phoneNumber}
                      onChange={e =>
                        setPhoneNumber(e.target.value.replace(/\D/g, ""))
                      }
                      className="rounded-l-none border-0 focus-visible:ring-0"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || phoneNumber.length !== 10}
                  className="mt-2 w-full rounded-xl py-6 font-semibold shadow-md"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      Send Verification OTP{" "}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>

                <div className="pt-2 text-center text-sm text-muted-foreground">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    search={{ redirectTo }}
                    className="font-semibold text-primary hover:underline"
                  >
                    Sign In
                  </Link>
                </div>
              </form>
            )}

            {step === 2 && (
              <form
                onSubmit={handleVerifyOtp}
                className="anim-fade-up space-y-6"
              >
                <div className="flex justify-center py-2">
                  <InputOTP
                    maxLength={6}
                    pattern={REGEXP_ONLY_DIGITS}
                    value={otp}
                    onChange={value => setOtp(value)}
                  >
                    <InputOTPGroup className="gap-2">
                      <InputOTPSlot
                        index={0}
                        className="rounded-lg border shadow-sm"
                      />
                      <InputOTPSlot
                        index={1}
                        className="rounded-lg border shadow-sm"
                      />
                      <InputOTPSlot
                        index={2}
                        className="rounded-lg border shadow-sm"
                      />
                      <InputOTPSlot
                        index={3}
                        className="rounded-lg border shadow-sm"
                      />
                      <InputOTPSlot
                        index={4}
                        className="rounded-lg border shadow-sm"
                      />
                      <InputOTPSlot
                        index={5}
                        className="rounded-lg border shadow-sm"
                      />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {timer > 0
                      ? `Resend code in 00:${timer < 10 ? `0${timer}` : timer}`
                      : "Didn't get code?"}
                  </span>
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={!canResend || loading}
                    className="flex items-center font-semibold text-primary hover:underline disabled:opacity-50"
                  >
                    <RefreshCw className="mr-1 h-3 w-3" /> Resend OTP
                  </button>
                </div>

                <div className="space-y-3">
                  <Button
                    type="submit"
                    disabled={loading || otp.length !== 6}
                    className="w-full rounded-xl py-6 font-semibold shadow-md"
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        Create Account <ShieldCheck className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>

                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex w-full items-center justify-center text-sm font-medium text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeft className="mr-1.5 h-4 w-4" /> Edit Account
                    Details
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
}
